import type { SessionBeat } from "./turo-world-session";
import { L2_SCENARIOS } from "./turo-world-l2-scenarios";

/**
 * Level 2's four sessions, as data — plan §3.3–§3.6's beat sheets.
 *
 * 🔴 THE "NOT TODAY" BRANCH IS THIS SAME BEAT LIST WITH THE CAMERA OFF. Plan
 * §3.2 requires the no-path to reach the same session-end node (validator rule
 * 8), and §8's reason is the level's whole purpose: *"If the no path is worse in
 * any perceptible way, the level has taught the child their no doesn't count,
 * which is the one thing it exists to prevent."*
 *
 * ⚠️ SO THERE IS NO SECOND BEAT LIST, AND THAT IS THE DESIGN. A shorter "no"
 * list is the obvious implementation and it is how the no-path quietly becomes
 * worse: someone trims a beat, nobody notices, and the child who declined gets
 * less. One list means the two paths cannot diverge without deleting this
 * comment. What changes on the no-path is that Turo demonstrates and the child
 * copies at their own pace — the beats, the praise and the goodbye are identical.
 *
 * 🔴 SESSION ORDER IS FIXED 1→2→3→4 (validator rule 13). §3.1: not shuffled, not
 * selectable. S1 earns the consent that S2 asks the child to exercise — a child
 * who has already had a "no" honoured is a different child by S3's refusal
 * lesson.
 *
 * ⚠️ EVERY SESSION ENDS ON A CELEBRATION, NEVER AN INSTRUCTION (rule 10), which
 * is why each list closes on `talk` carrying `core.bye` rather than on a pose.
 *
 * 🔴 THERE IS NO CONSENT BEAT ANY MORE, AND THAT IS BECAUSE NOTHING WATCHES.
 * §3.2 opened every session with *"I would love to watch you move. Is that okay?
 * You can say no."* — a question about being observed, whose "not today" branch
 * turned the camera off. This level has no camera, no tracking, no mirror and no
 * microphone. Asking permission for something that does not happen is worse than
 * not asking: it introduces the idea of being watched on the one screen where
 * nobody is, and teaches that consent is a formality you tap past.
 *
 * 📋 IF A CAMERA RETURNS TO L2, THE CONSENT BEAT RETURNS WITH IT, FIRST. The
 * lines stay recorded and in the voice table so that is a wiring change.
 */


const talk = (...lines: string[]): SessionBeat => ({
  kind: "talk",
  level: 2,
  lines,
});

/** DO / WATCH / FREE are all `pose` — the POSE says which. See `SessionBeat`. */
const pose = (poseId: string, ...lines: string[]): SessionBeat => ({
  kind: "pose",
  level: 2,
  poseId,
  lines,
});

/* ────────────────────── S1 · Hello my way — eight choices ── */

/**
 * 🔴 S1 IS SIX CHOICES, NOT A POSE DRILL. §3.3 called the single choose
 * beat *"the point of the session"*; with tracking removed the demonstrations
 * around it had nothing for a child to do, so the session became a slideshow.
 * Every beat here asks the child to decide, and every answer is honoured.
 *
 * ⚠️ SCREEN ONLY — no camera, no microphone. §6 keeps voice one-way.
 */
/** Six per round, like every game: these two repeat the father's namaste and the friend's wave. */
const S1_HELD_BACK: readonly string[] = ["l2-elder", "l2-final"];

export const L2_S1: readonly SessionBeat[] = [
  talk("core.greet", "body.s1.open"),
  ...L2_SCENARIOS.filter((sc) => !S1_HELD_BACK.includes(sc.id)).map(
    (sc): SessionBeat => ({
      kind: "choose",
      level: 2,
      scenarioId: sc.id,
      /*
       * The framing and the one thing we do, spoken while the card is up.
       * "Now do it with me" comes later, from the component, as Turo takes the
       * screen — saying it here would land while the child is still looking at
       * a picture.
       */
      lines: [...sc.open, sc.teach],
    }),
  ),
  talk("body.s1.close", "core.bye"),
];

/* ──────────────────────────────── S2 · My body is mine ── */

export const L2_S2: readonly SessionBeat[] = [
  talk("core.greet", "body.s2.open"),
  pose("hands_on_hips", "body.boss.cue", "body.boss.concept"),
  pose("arms_folded", "body.mine.cue", "body.mine.concept"),
  /**
   * ⚠️ SCHOOL-TOGGLEABLE, DEFAULT OFF (plan §9 decision 9). *"The parts under my
   * clothes are private. They belong to me."* — one verbal line, never drawn.
   * It stays in the list rather than being omitted so it exists in the audio
   * manifest and the reviewer package either way, and switching it on never
   * triggers a content download.
   */
  { ...talk("body.clothes"), optional: true },
  pose("self_hug", "body.selfhug.cue", "body.selfhug.concept"),
  pose("thumb_to_chest", "body.thatsme.cue", "body.thatsme.concept"),
  talk("body.s2.close", "core.bye"),
];

/* ──────────────────── S3 · Safe touches, and no thank you ── */

/**
 * 🔴 THE POSITIVE HALF COMES FIRST, AND THE ORDER IS THE TEACHING. Plan §3.5:
 * *"A child who learns refusal before they have a picture of safe touch becomes
 * fearful of contact."* The first four poses are warmth; only then does the
 * level teach saying no.
 *
 * ⚠️ SIX PER ROUND, LIKE EVERY GAME: `two_thumbs_up` is held back, the one
 * warmth pose that is not a touch.
 */
export const L2_S3: readonly SessionBeat[] = [
  talk("core.greet", "body.s3.open"),
  pose("high_five", "body.b1.concept"),
  pose("hug_mother", "core.watchme", "body.hug.concept"),
  pose("holding_hands", "body.hands.concept"),
  pose("teacher_welldone", "body.teacher.concept"),
  /**
   * 🔴 THE LAST TWO ARE A DELIBERATE PAIR — the everyday refusal and the firm
   * one. §3.5: *"Most refusals a child needs are the first kind. Teaching only
   * the firm version implies refusal is for emergencies, so it never gets used
   * on an ordinary Tuesday."*
   */
  pose("no_thank_you", "body.nothankyou.cue", "body.nothankyou.concept"),
  pose("say_no_calm", "body.no.cue", "body.no.concept"),
  talk("body.s3.close", "core.bye"),
];

/* ───────────────────────── S4 · My tummy and my people ── */

export const L2_S4: readonly SessionBeat[] = [
  talk("core.greet", "body.s4.open"),
  /**
   * 🔴 THE GESTURE, NEVER THE FACE. Plan §3.6: *"'Put your hands on your tummy —
   * that's where the uh-oh feeling lives' is fine. 'Show me a worried face' is
   * not: it asks a preschooler to perform distress on command and be watched
   * doing it."* No cue in this level asks a child to look scared, sad or
   * worried — Turo's sprite carries the expression, the child carries the hands.
   */
  pose("hands_on_tummy", "body.uhoh.cue", "body.uhoh.concept"),
  pose("hands_on_heart", "body.safe.cue", "body.safe.concept"),
  pose("five_people", "body.five.cue", "body.five.concept"),
  pose("tell_adult", "body.tell.concept"),
  /**
   * 🔴 THE TWO LINES A CHILD CARRIES OUT OF THE ROOM. §3.6: *"it is never your
   * fault"*, and then — *"if the first person is busy, tell somebody else. Keep
   * telling until somebody listens."* The second exists because first
   * disclosures get brushed off, and a child who tries once and is dismissed
   * usually doesn't try again.
   */
  talk("body.notfault", "body.keeptelling"),
  talk("body.s4.close", "core.bye"),
];

/** Fixed order — validator rule 13. Not shuffled, not selectable. */
export const L2_SESSIONS: readonly (readonly SessionBeat[])[] = [
  L2_S1,
  L2_S2,
  L2_S3,
  L2_S4,
];
